import { renderGame, renderCharTiles, setCommentary } from '../ui/render.js';
import { dingYourTurn } from '../ui/sound.js';
import { newGameState } from '../engine/deal.js';
import { createRng, makeSeed } from '../engine/rng.js';
import {
  applyCharlestonPass,
  maybeEndCharleston,
  skipSecondCharleston,
  applyCourtesyRound,
} from '../engine/charleston.js';
import {
  applyDraw,
  applyDiscard,
  advanceTurn,
  claimOptions,
  applyClaim,
  applyMahjong,
  jokerExchangeOptions,
  applyJokerExchange,
} from '../engine/turn.js';
import {
  aiPickDiscard,
  aiDecideClaim,
  aiExplainDiscard,
  aiShouldDeclare,
  aiDoJokerSwaps,
  visibleTileCounts,
  aiWantsSecondCharleston,
  aiCourtesyPick,
} from '../engine/ai.js';
import { rankTargets, targetNeeds } from '../engine/patterns.js';
import { tileName, sortHand } from '../engine/tiles.js';
import { HUMAN_INDEX, PLAYER_COUNT } from '../engine/constants.js';

// Local-only mode. Authoritative state is held here in-module; AI pacing is
// driven by setTimeout. In online mode (Phase 4), this module is replaced by
// a thin wrapper that calls submit_move and re-renders on realtime updates.

let state = null;
let ui = {
  selectedTile: -1,
  claimPending: false,
  charPicked: [],
  hintPattern: null, // current target — click 💡 Hint to cycle through top 3
  hintIdx: -1,
};
let difficulty = 'beginner';
let onExitCallback = null;

export function startLocalGame({ difficulty: diff = 'beginner', onExit }) {
  difficulty = diff;
  onExitCallback = onExit;
  ui = {
    selectedTile: -1, claimPending: false, charPicked: [], hintPattern: null, hintIdx: -1,
    charMode: 'pass', secondCharDecided: false, courtesyDone: false,
  };
  const seed = makeSeed();
  const rng = createRng(seed);
  state = newGameState({ seed, rng, difficulty });
  setCommentary('Welcome! Time for the Charleston — select 3 tiles to pass.');
  render();
  showCharOverlay();
}

function render() {
  // 🃏 Swap button shows only when the human can legally exchange right now.
  ui.swapCount =
    state.phase === 'play' &&
    state.currentPlayer === HUMAN_INDEX &&
    state.hasDrawn &&
    !ui.claimPending
      ? jokerExchangeOptions(state, HUMAN_INDEX).length
      : 0;
  renderGame(state, ui, () => canDeclare());
}

function canDeclare() {
  if (!state || state.phase !== 'play') return false;
  if (state.currentPlayer !== HUMAN_INDEX) return false;
  if (!state.hasDrawn) return false;
  const p = state.players[HUMAN_INDEX];
  let total = p.hand.length;
  for (const g of p.exposed) total += g.tiles.length;
  return total >= 14;
}

// ---------- Charleston ----------
// Flow: passes 1–3 (required) → all-four-agree decision → passes 4–6 (or
// skip) → courtesy pass (0–3 tiles with the player across) → play.
function showCharOverlay() {
  const skipBtn = document.getElementById('charSkipBtn');
  if (state.charStep === 3 && !ui.secondCharDecided) {
    showSecondCharDecision();
    return;
  }
  if (state.charStep >= 6 && !ui.courtesyDone) {
    showCourtesyOverlay();
    return;
  }
  skipBtn.style.display = 'none';
  if (maybeEndCharleston(state)) {
    document.getElementById('charOverlay').classList.remove('show');
    setCommentary(
      (ui.courtesyNote || '') + 'Charleston complete! East goes first. Watch and wait for your turn.',
    );
    ui.courtesyNote = null;
    render();
    setTimeout(runAiTurn, 1100);
    return;
  }
  ui.charMode = 'pass';
  ui.charPicked = [];
  const ov = document.getElementById('charOverlay');
  ov.classList.add('show');
  document.getElementById('charTitle').textContent = 'Charleston — Pass ' + (state.charStep + 1) + ' of 6';
  document.getElementById('charDesc').textContent = [
    'Pass 3 tiles RIGHT →',
    'Pass 3 tiles ACROSS ↕',
    'Pass 3 tiles LEFT ←',
    'Second pass: 3 LEFT ←',
    'Second pass: 3 ACROSS ↕',
    'Second pass: 3 RIGHT →',
  ][state.charStep];
  document.getElementById('charBtn').textContent = 'Pass Tiles';
  renderCharTiles(state, ui.charPicked);
}

// "Second Charleston?" decision — NMJL: all four must agree; any player stops it.
function showSecondCharDecision() {
  ui.charMode = 'decision';
  document.getElementById('charOverlay').classList.add('show');
  document.getElementById('charTitle').textContent = 'Second Charleston?';
  document.getElementById('charDesc').textContent =
    'The required Charleston is done. Keep passing (3 more rounds)? ' +
    'All four players must agree — any player can stop it.';
  document.getElementById('charTiles').innerHTML = '';
  document.getElementById('charCount').textContent = '';
  const btn = document.getElementById('charBtn');
  btn.disabled = false;
  btn.textContent = 'Yes — Keep Passing';
  const skipBtn = document.getElementById('charSkipBtn');
  skipBtn.style.display = '';
  skipBtn.textContent = 'Stop Here';
}

function decideSecondCharleston(humanWants) {
  ui.secondCharDecided = true;
  if (!humanWants) {
    skipSecondCharleston(state);
    setCommentary('You stopped the passing. On to the courtesy pass.');
    showCharOverlay();
    return;
  }
  const stopper = [1, 2, 3].find((i) => !aiWantsSecondCharleston(state.players[i]));
  if (stopper != null) {
    skipSecondCharleston(state);
    setCommentary(state.players[stopper].name + ' stopped the passing. On to the courtesy pass.');
  } else {
    setCommentary('Everyone agreed — second Charleston!');
  }
  showCharOverlay();
}

// Courtesy pass: offer 0–3 tiles to the player across; the pair exchanges
// the lesser of the two offers.
function showCourtesyOverlay() {
  ui.charMode = 'courtesy';
  ui.charPicked = [];
  document.getElementById('charOverlay').classList.add('show');
  document.getElementById('charSkipBtn').style.display = 'none';
  const across = state.players[2]; // seats pair across: index 0 ↔ index 2
  document.getElementById('charTitle').textContent = 'Courtesy Pass';
  document.getElementById('charDesc').textContent =
    `Offer 0–3 tiles to ${across.name} (across the table). ` +
    "You'll exchange the SMALLER of your two offers — extras stay with you.";
  renderCourtesyTiles();
}

function renderCourtesyTiles() {
  renderCharTiles(state, ui.charPicked);
  document.getElementById('charCount').textContent = ui.charPicked.length + ' / up to 3 selected';
  const btn = document.getElementById('charBtn');
  btn.disabled = false;
  btn.textContent =
    ui.charPicked.length === 0
      ? 'Offer No Tiles'
      : `Offer ${ui.charPicked.length} Tile${ui.charPicked.length > 1 ? 's' : ''}`;
}

function doCourtesyPass() {
  const offers = {};
  offers[state.players[HUMAN_INDEX].seat] = ui.charPicked.map(
    (i) => state.players[HUMAN_INDEX].hand[i].id,
  );
  for (let i = 1; i < PLAYER_COUNT; i++) {
    offers[state.players[i].seat] = aiCourtesyPick(state.players[i]).map((t) => t.id);
  }
  const exchanged = applyCourtesyRound(state, offers);
  ui.courtesyDone = true;
  const across = state.players[2];
  const n = exchanged[state.players[HUMAN_INDEX].seat] || 0;
  ui.courtesyNote =
    n === 0
      ? 'No courtesy tiles exchanged. '
      : `You and ${across.name} exchanged ${n} tile${n > 1 ? 's' : ''}. `;
  render();
  showCharOverlay();
}

export function charPickTile(idx) {
  const pos = ui.charPicked.indexOf(idx);
  if (pos >= 0) ui.charPicked.splice(pos, 1);
  else if (ui.charPicked.length < 3) ui.charPicked.push(idx);
  if (ui.charMode === 'courtesy') renderCourtesyTiles();
  else renderCharTiles(state, ui.charPicked);
}

export function charPass() {
  if (ui.charMode === 'courtesy') {
    doCourtesyPass();
    return;
  }
  if (ui.charMode === 'decision') {
    decideSecondCharleston(true);
    return;
  }
  if (ui.charPicked.length !== 3) return;
  const ids = ui.charPicked.map((i) => state.players[HUMAN_INDEX].hand[i].id);
  applyCharlestonPass(state, ids);
  render();
  showCharOverlay();
}

export function charSkip() {
  if (ui.charMode !== 'decision') return;
  decideSecondCharleston(false);
}

// ---------- Player actions ----------
export function selectTileAt(idx) {
  if (state.phase !== 'play') return;
  ui.selectedTile = ui.selectedTile === idx ? -1 : idx;
  render();
}

export function playerDraw() {
  if (state.currentPlayer !== HUMAN_INDEX || state.hasDrawn || state.wall.length === 0) return;
  const { tile } = applyDraw(state, HUMAN_INDEX);
  ui.selectedTile = -1;
  ui.recentDrawId = tile.id;
  const swapTip = jokerExchangeOptions(state, HUMAN_INDEX).length
    ? ' 🃏 You can swap a tile for an exposed Joker!'
    : '';
  setCommentary('You drew: ' + tileName(tile) + '. Select a tile to discard.' + swapTip);
  setTimeout(() => {
    if (ui.recentDrawId === tile.id) {
      ui.recentDrawId = null;
      render();
    }
  }, 3000);
  render();
}

export function playerDiscard() {
  if (ui.selectedTile < 0 || !state.hasDrawn) return;
  const tileId = state.players[HUMAN_INDEX].hand[ui.selectedTile].id;
  const { tile } = applyDiscard(state, HUMAN_INDEX, tileId);
  ui.selectedTile = -1;
  setCommentary('You discarded ' + tileName(tile) + '.');
  // AI claim check on the human discard
  for (let i = 1; i <= 3; i++) {
    const kind = aiDecideClaim(state.players[i], tile, difficulty);
    if (kind) {
      setTimeout(() => {
        applyClaim(state, i, kind);
        setCommentary(state.players[i].name + ' claimed ' + tileName(tile) + ' for a ' + kind.toUpperCase() + '!');
        render();
        setTimeout(runAiTurn, 1100);
      }, 500);
      render();
      return;
    }
  }
  advanceTurn(state);
  render();
  setTimeout(runAiTurn, 900);
}

export function showHint() {
  const player = state.players[HUMAN_INDEX];
  const hand = player.hand;
  const ranked = rankTargets(hand, player.exposed.length > 0).slice(0, 3);
  if (ranked.length === 0) {
    ui.hintPattern = null;
    ui.hintIdx = -1;
    setCommentary('💡 Hint: Focus on one or two suits and the tiles a card hand needs. Discard lone winds and odd tiles.');
    render();
    return;
  }
  // Cycle: -1 → 0 → 1 → 2 → -1 (cleared) → 0 …
  ui.hintIdx = (ui.hintIdx + 1) % (ranked.length + 1);
  if (ui.hintIdx === ranked.length) {
    ui.hintPattern = null;
    setCommentary('💡 Hint cleared. Click again to cycle top targets.');
  } else {
    const t = ranked[ui.hintIdx];
    ui.hintPattern = t.hand; // the real 2026 card hand
    const needs = targetNeeds(t, hand);
    setCommentary(`💡 Target ${ui.hintIdx + 1}/${ranked.length}: ${t.section} — ${t.label} (${t.value}${t.concealed ? ' C' : ''}). Need: ${needs}. Click 💡 to cycle.`);
  }
  render();
}

// 🃏 Swap button: list every legal joker exchange in the claim-prompt panel.
// Only shown on the human's turn after drawing (see render()); mutually
// exclusive with real claim prompts, so reusing the panel is safe.
export function showSwapOptions() {
  const options = jokerExchangeOptions(state, HUMAN_INDEX);
  if (!options.length) return;
  const el = document.getElementById('claimPrompt');
  const pass = document.getElementById('claimPass');
  el.classList.add('show');
  document.getElementById('claimMsg').textContent =
    'Swap a tile from your hand for an exposed Joker:';
  const actions = document.getElementById('claimActions');
  actions.innerHTML = '';
  for (const opt of options) {
    const owner = state.players[opt.ownerIdx];
    const group = owner.exposed[opt.groupIdx];
    const natural = group.tiles.find((t) => t.type !== 'joker');
    const whose = opt.ownerIdx === HUMAN_INDEX ? 'your' : owner.name + "'s";
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn-claim';
    btn.textContent = `${tileName(natural)} → 🃏 (${whose} ${opt.kind})`;
    btn.onclick = () => {
      el.classList.remove('show');
      pass.textContent = 'Pass';
      applyJokerExchange(state, HUMAN_INDEX, opt.ownerIdx, opt.groupIdx);
      setCommentary(
        `You swapped your ${tileName(natural)} for the Joker in ${whose} ${opt.kind}!` +
          (jokerExchangeOptions(state, HUMAN_INDEX).length ? ' 🃏 Another swap is available.' : ''),
      );
      render();
    };
    actions.appendChild(btn);
  }
  pass.textContent = 'Cancel';
  pass.onclick = () => {
    el.classList.remove('show');
    pass.textContent = 'Pass';
  };
}

export function declareMahjong() {
  if (!canDeclare()) return;
  const player = state.players[HUMAN_INDEX];
  try {
    applyMahjong(state, HUMAN_INDEX);
    render();
    const w = state.winner;
    showWin(`Mahjong! ${player.name} won with ${w.handSection} — ${w.handLabel} (${w.value} points)!`, true);
  } catch (err) {
    if (err.message === 'hand_incomplete') {
      setCommentary("That's not a complete 2026 hand yet. Tap 💡 to see the closest card hands and what you still need.");
    } else {
      setCommentary('Cannot declare Mahjong right now.');
    }
  }
}

// ---------- AI loop ----------
function runAiTurn() {
  if (!state || state.phase !== 'play' || state.currentPlayer === HUMAN_INDEX) return;
  const p = state.players[state.currentPlayer];

  // Draw if needed.
  if (!state.hasDrawn) {
    if (state.wall.length === 0) {
      wallGame();
      return;
    }
    const { tile: drawn } = applyDraw(state, state.currentPlayer);
    if (!drawn) return;
  }

  setTimeout(() => {
    // NMJL joker exchange: trade spare naturals for exposed jokers first —
    // a joker in hand is strictly more flexible. Beginner AIs skip this.
    let swapNote = '';
    if (difficulty !== 'beginner') {
      const swaps = aiDoJokerSwaps(state, state.currentPlayer);
      if (swaps.length) {
        const s = swaps[0];
        const whose = s.ownerIdx === HUMAN_INDEX ? 'your' : s.ownerIdx === state.currentPlayer ? 'their own' : state.players[s.ownerIdx].name + "'s";
        swapNote = `${p.name} swapped a ${tileName(s.given)} for the Joker in ${whose} exposure! `;
        if (swaps.length > 1) swapNote = `${p.name} swapped for ${swaps.length} exposed Jokers! `;
      }
    }
    // Declare Mahjong if this draw/claim/swap completed a real 2026 hand.
    const win = aiShouldDeclare(p);
    if (win) {
      applyMahjong(state, state.currentPlayer);
      render();
      const w = state.winner;
      showWin(`${p.name} declared Mahjong with ${w.handSection} — ${w.handLabel} (${w.value} points). Better luck next round!`, false);
      return;
    }
    // Pick + apply a discard using pure engine helpers.
    const rng = createRng((state.seed ^ state.discards.length ^ state.currentPlayer) >>> 0);
    const pick = aiPickDiscard(p, difficulty, rng, difficulty === 'advanced' ? visibleTileCounts(state) : null);
    applyDiscard(state, state.currentPlayer, pick.id);
    const why = aiExplainDiscard(p, pick);
    setCommentary(swapNote + p.name + ' discarded ' + tileName(pick) + '.' + (!swapNote && difficulty !== 'advanced' ? ' ' + why : ''));

    // Check if human can claim.
    const humanHand = state.players[HUMAN_INDEX];
    const humanOptions = claimOptions(humanHand, pick);
    if (humanOptions.length) {
      showClaimPrompt(pick, humanOptions);
      render();
      return;
    }

    // Check if another AI wants to claim.
    for (let i = 1; i < PLAYER_COUNT; i++) {
      if (i === state.currentPlayer) continue;
      const kind = aiDecideClaim(state.players[i], pick, difficulty);
      if (kind) {
        applyClaim(state, i, kind);
        setCommentary(state.players[i].name + ' claimed ' + tileName(pick) + ' for a ' + kind.toUpperCase() + '!');
        render();
        setTimeout(runAiTurn, 1100);
        return;
      }
    }

    advanceTurn(state);
    if (state.currentPlayer === HUMAN_INDEX) {
      setCommentary('Your turn! Tap "Draw" to draw a tile from the wall.');
      dingYourTurn();
      render();
    } else {
      render();
      setTimeout(runAiTurn, 1300);
    }
  }, 700 + Math.random() * 500);
}

function showClaimPrompt(tile, options) {
  ui.claimPending = true;
  const el = document.getElementById('claimPrompt');
  el.classList.add('show');
  document.getElementById('claimMsg').textContent =
    state.players[state.currentPlayer].name + ' discarded ' + tileName(tile) + '. Claim it?';

  // One button per legal option (Pung / Kong); rebuilt each prompt.
  const actions = document.getElementById('claimActions');
  actions.innerHTML = '';
  for (const kind of options) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn-claim';
    btn.textContent = kind === 'kong' ? 'Kong' : 'Pung';
    btn.onclick = () => {
      el.classList.remove('show');
      ui.claimPending = false;
      applyClaim(state, HUMAN_INDEX, kind);
      sortHand(state.players[HUMAN_INDEX].hand);
      const swapTip = jokerExchangeOptions(state, HUMAN_INDEX).length
        ? ' 🃏 You can swap a tile for an exposed Joker!'
        : '';
      setCommentary('You claimed ' + tileName(tile) + ' for a ' + kind.toUpperCase() + '! Now discard a tile.' + swapTip);
      render();
    };
    actions.appendChild(btn);
  }

  document.getElementById('claimPass').onclick = () => {
    el.classList.remove('show');
    ui.claimPending = false;
    advanceTurn(state);
    if (state.currentPlayer === HUMAN_INDEX) {
      setCommentary('Your turn! Tap "Draw" to draw a tile.');
      dingYourTurn();
      render();
    } else {
      render();
      setTimeout(runAiTurn, 900);
    }
  };
}

function showWin(msg, isHuman) {
  document.getElementById('winMsg').textContent = msg;
  document.getElementById('winOverlay').classList.add('show');
  if (isHuman) spawnConfetti();
}

export function hideWin() {
  document.getElementById('winOverlay').classList.remove('show');
  document.querySelectorAll('.confetti').forEach((c) => c.remove());
}

function spawnConfetti() {
  const colors = ['#c41e3a', '#1a6847', '#d4a843', '#003f87', '#6b2c91', '#ff6b35'];
  for (let i = 0; i < 60; i++) {
    const c = document.createElement('div');
    c.className = 'confetti';
    c.style.left = Math.random() * 100 + 'vw';
    c.style.background = colors[Math.floor(Math.random() * colors.length)];
    c.style.width = 6 + Math.random() * 8 + 'px';
    c.style.height = 6 + Math.random() * 8 + 'px';
    c.style.animationDelay = Math.random() * 2 + 's';
    c.style.animationDuration = 2 + Math.random() * 2 + 's';
    document.body.appendChild(c);
  }
  setTimeout(() => document.querySelectorAll('.confetti').forEach((c) => c.remove()), 5000);
}

function wallGame() {
  state.phase = 'done';
  setCommentary('Wall game! No tiles remain. Nobody wins this round.');
  render();
  setTimeout(() => showWin('Wall game — the wall ran out of tiles! No winner this round.', false), 1000);
}

export function exitGame() {
  state = null;
  if (onExitCallback) onExitCallback();
}
